import { lookupsJson } from '@ourbranch/lookups';

// Home characteristics an agent may enter against our data (github issues #26706, #35350). To call out
// another one, add it here and place a <DataSourceMismatchNotification name="..." /> under its field.
export const overridableHomeFields = {
  yearBuilt: { label: 'year built', resetLabel: 'Reset year built', parse: Number },
  sqFt: {
    label: 'above-ground square footage',
    resetLabel: 'Reset Above-ground sq. ft.',
    parse: Number,
    describe: (value) => `${value} square feet`
  },
  constructionType: {
    label: 'construction type',
    resetLabel: 'Reset construction type',
    describe: (value) => lookupsJson.constructionType.find(({ id }) => id === value)?.value ?? value
  },
  roofShape: {
    label: 'roof shape',
    resetLabel: 'Reset roof shape',
    describe: (value) => lookupsJson.roofShape.find(({ id }) => id === value)?.value ?? value
  }
};

// What our data indicates for a characteristic (offer.characteristicsData.dataValues, stored as strings
// by the rater), parsed to the form field's type; undefined when we have no data value for it.
export const getDataValue = (offer, name) => {
  const dataValues = offer?.characteristicsData?.dataValues;
  // Offers rated before dataValues existed still carry originalSqFt; use it so their sqFt callout and event keep
  // working until the next re-rate. Transitional: remove once offers rated before #35525 are past the 60-day
  // OLD_OFFER limit (same condition as getHomeRatedDiff, #35751), together with originalSqFt (#35752).
  if (!dataValues && name === 'sqFt') {
    return offer?.quote?.home?.originalSqFt || undefined;
  }

  const dataValue = dataValues?.find((entry) => entry.name === name)?.dataValue;
  if (dataValue == null) {
    return undefined;
  }

  const { parse = (value) => value } = overridableHomeFields[name] ?? {};
  return parse(dataValue);
};

// sqFt keeps its own 'Above-ground sq. ft. different than data-provided value' event
const fieldsInGenericEvent = Object.keys(overridableHomeFields).filter((name) => name !== 'sqFt');

// Fields this save changes away from our data, for the 'Home characteristic different than data-provided
// value' event: only on the save that changes the field, not on later saves of an already-overridden offer.
// Values are strings so each event property has a single type.
export const getDataValueChanges = (offer, savedHome) =>
  fieldsInGenericEvent.flatMap((characteristic) => {
    const originalValue = getDataValue(offer, characteristic);
    const value = savedHome?.[characteristic];
    if (
      originalValue == null ||
      value == null ||
      value === originalValue ||
      value === offer?.quote?.home?.[characteristic]
    ) {
      return [];
    }

    return [{ characteristic, value: String(value), originalValue: String(originalValue) }];
  });
